import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Layout from "../../../components/Layout";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  accent: '#4FC3F7',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3'
};

const EditIncidentType = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    type_incident: ""
  });
  
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [originalData, setOriginalData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  // États pour les erreurs d'abonnement
  const [abonnementError, setAbonnementError] = useState(false);
  const [abonnementMessage, setAbonnementMessage] = useState("");

  // Fonction pour réinitialiser les erreurs d'abonnement
  const resetAbonnementError = () => {
    setAbonnementError(false);
    setAbonnementMessage("");
  };

  // Récupérer les données du type d'incident à modifier
  useEffect(() => {
    const fetchIncidentType = async () => {
      if (!id) return;
      
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          setError("Session expirée. Veuillez vous reconnecter.");
          setTimeout(() => navigate("/login"), 2000);
          setFetching(false);
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/incidents/type_incident/get_type_incidents.php?id=${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            },
          }
        );

        const data = await response.json();

        // 🔍 Vérifier si c'est une erreur 403 avec code ABONNEMENT_REQUIS
        if (response.status === 403 && data.code === 'ABONNEMENT_REQUIS') {
          setAbonnementError(true);
          setAbonnementMessage(data.message || "Abonnement inactif");
          setFetching(false);
          return;
        }

        if (data.success) {
          const incidentType = data.type_incident || {};
          setOriginalData(incidentType);
          setFormData({
            type_incident: incidentType.type_incident || ""
          });
        } else {
          setError(data.message || "Erreur lors du chargement du type d'incident");
          setTimeout(() => {
            navigate("/typeIncidents");
          }, 2000);
        }
      } catch (err) {
        setError("Erreur serveur: " + err.message);
        setTimeout(() => {
          navigate("/typeIncidents");
        }, 2000);
      } finally {
        setFetching(false);
      }
    };

    fetchIncidentType();
  }, [id, navigate]);

  // Validation en temps réel
  useEffect(() => {
    validateField();
  }, [formData.type_incident]);

  const validateField = () => {
    const errors = {};

    if (!formData.type_incident.trim()) {
      errors.type_incident = "Le type d'incident est requis";
    } else if (formData.type_incident.trim().length > 250) {
      errors.type_incident = "Maximum 250 caractères";
    } else if (formData.type_incident.trim().length < 2) {
      errors.type_incident = "Minimum 2 caractères";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation avant soumission
    if (!validateField()) {
      setError("Veuillez corriger les erreurs avant de soumettre");
      return;
    }

    setLoading(true);
    setError("");
    setValidationErrors([]);
    resetAbonnementError();

    try {
      const token = localStorage.getItem("access_token");

      const response = await fetch(
        `${API_BASE_URL}/incidents/type_incident/update_type_incidents.php?id=${id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ type_incident: formData.type_incident.trim() }),
        }
      );

      const data = await response.json();

      // 🔍 Vérifier si c'est une erreur 403 avec code ABONNEMENT_REQUIS
      if (response.status === 403 && data.code === 'ABONNEMENT_REQUIS') {
        setAbonnementError(true);
        setAbonnementMessage(data.message || "Abonnement inactif");
        setLoading(false);
        return;
      }

      if (data.success) {
        setSuccessMsg("Type d'incident modifié avec succès !");
        
        setTimeout(() => {
          navigate("/typeIncidents");
        }, 2000);
      } else {
        if (data.errors && Array.isArray(data.errors)) {
          setValidationErrors(data.errors);
        } else {
          setError(data.message || "Erreur lors de la modification");
        }
      }
    } catch (err) {
      setError("Erreur serveur : " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (originalData) {
      setFormData({
        type_incident: originalData.type_incident || ""
      });
    }
    setError("");
    setSuccessMsg("");
    setValidationErrors([]);
    setFieldErrors({});
    resetAbonnementError();
  };

  const handleCancel = () => {
    navigate("/typeIncidents");
  };

  // Affichage de l'erreur d'abonnement
  if (abonnementError) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Modifier le type d'incident</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/typeIncidents">Types d'incidents</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Erreur</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="row justify-content-center">
            <div className="col-lg-8 col-md-10">
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none'
              }}>
                <div className="card-body">
                  <div className="alert alert-warning alert-dismissible fade show" style={{ 
                    backgroundColor: '#fff3e0',
                    borderColor: COLORS.warning,
                    color: '#ff8f00',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.warning}`,
                    whiteSpace: "pre-line"
                  }}>
                    <div className="d-flex align-items-start">
                      <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                      <div className="flex-grow-1">
                        {abonnementMessage}
                        <div className="d-flex gap-2 justify-content-center mt-4">
                          <a 
                            href="/abonnements/create"
                            className="btn fw-semibold"
                            style={{
                              backgroundColor: '#4caf50',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '8px 20px',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                          >
                            <i className="bi bi-box-arrow-up-right"></i>
                            Souscrire à un abonnement
                          </a>
                          <button
                            className="btn fw-semibold"
                            onClick={() => navigate("/typeIncidents")}
                            style={{
                              backgroundColor: '#f44336',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '8px 20px'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                          >
                            Retour
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (fetching) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Modifier le type d'incident</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/typeIncidents">Types d'incidents</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Chargement...</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="row justify-content-center">
            <div className="col-lg-8 col-md-10">
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none'
              }}>
                <div className="card-body text-center py-5">
                  <div className="spinner-border" style={{ color: COLORS.primary }} role="status"></div>
                  <p className="mt-3" style={{ color: COLORS.darkText }}>Chargement du type d'incident...</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (error && !fetching && !originalData) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Modifier le type d'incident</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/typeIncidents">Types d'incidents</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Erreur</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="row justify-content-center">
            <div className="col-lg-8 col-md-10">
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none'
              }}>
                <div className="card-body">
                  <div className="alert alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#ffebee',
                    borderColor: COLORS.danger,
                    color: '#c62828',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.danger}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <div className="flex-grow-1">{error}</div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setError("")}
                      ></button>
                    </div>
                  </div>
                  <div className="text-center">
                    <button 
                      className="btn fw-semibold"
                      onClick={() => navigate("/typeIncidents")}
                      style={{
                        color: COLORS.primary,
                        borderColor: COLORS.primary,
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '10px 24px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = COLORS.primary;
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = COLORS.primary;
                      }}
                    >
                      <i className="bi bi-arrow-left me-2"></i>
                      Retour à la liste
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>
          {originalData?.type_incident ? `${originalData.type_incident} / Modifier` : "Modifier le type d'incident"}
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/typeIncidents" style={{ color: COLORS.primary }}>Types d'incidents</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Modifier</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row justify-content-center">
          <div className="col-lg-8 col-md-10">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <h5 className="card-title" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  marginBottom: '24px'
                }}>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                        width: '36px',
                        height: '36px',
                        backgroundColor: '#fff3e0',
                        color: '#f57c00'
                      }}>
                        <i className="bi bi-pencil-square"></i>
                      </div>
                      Modification du type d'incident
                    </div>
                    <button
                      type="submit"
                      form="editIncidentTypeForm"
                      className="btn fw-semibold"
                      disabled={loading || Object.keys(fieldErrors).length > 0 || !formData.type_incident.trim()}
                      style={{
                        color: 'white',
                        backgroundColor: loading || Object.keys(fieldErrors).length > 0 || !formData.type_incident.trim() ? '#B0BEC5' : '#f57c00',
                        borderColor: loading || Object.keys(fieldErrors).length > 0 || !formData.type_incident.trim() ? '#B0BEC5' : '#f57c00',
                        borderRadius: '8px',
                        padding: '8px 20px',
                        fontSize: '0.9rem',
                        transition: 'all 0.3s ease',
                        opacity: loading ? 0.8 : 1,
                        minWidth: '44px'
                      }}
                      onMouseEnter={(e) => {
                        if (!loading && Object.keys(fieldErrors).length === 0 && formData.type_incident.trim()) {
                          e.target.style.backgroundColor = '#ff9800';
                          e.target.style.borderColor = '#ff9800';
                          e.target.style.transform = 'translateY(-2px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!loading && Object.keys(fieldErrors).length === 0 && formData.type_incident.trim()) {
                          e.target.style.backgroundColor = '#f57c00';
                          e.target.style.borderColor = '#f57c00';
                          e.target.style.transform = 'translateY(0)';
                        }
                      }}
                      title="Enregistrer les modifications"
                    >
                      {loading ? (
                        <span className="spinner-border spinner-border-sm" role="status"></span>
                      ) : (
                        <i className="bi bi-check-lg"></i>
                      )}
                    </button>
                  </div>
                </h5>

                {/* Messages */}
                {successMsg && (
                  <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#e8f5e9',
                    borderColor: COLORS.success,
                    color: '#2e7d32',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.success}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-check-circle me-2" style={{ fontSize: '1.2rem' }}></i>
                      <div className="flex-grow-1">
                        {successMsg}
                        <div className="mt-2">
                          <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Chargement...</span>
                          </div>
                          Redirection en cours...
                        </div>
                      </div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setSuccessMsg("")}
                      ></button>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#ffebee',
                    borderColor: COLORS.danger,
                    color: '#c62828',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.danger}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <div className="flex-grow-1">{error}</div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setError("")}
                      ></button>
                    </div>
                  </div>
                )}

                {validationErrors.length > 0 && (
                  <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#fff8e1',
                    borderColor: COLORS.warning,
                    color: '#ff8f00',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.warning}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <div className="flex-grow-1">
                        <strong>Veuillez corriger les erreurs suivantes :</strong>
                        <ul className="mb-0 mt-2">
                          {validationErrors.map((err, index) => (
                            <li key={index}>{err}</li>
                          ))}
                        </ul>
                      </div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setValidationErrors([])}
                      ></button>
                    </div>
                  </div>
                )}

                {/* Formulaire */}
                <form id="editIncidentTypeForm" onSubmit={handleSubmit}>
                  {/* Informations principales */}
                  <div className="card mb-4" style={{ 
                    borderRadius: '8px',
                    border: `1px solid ${COLORS.lightBg}`,
                    boxShadow: 'none'
                  }}>
                    <div className="card-header" style={{ 
                      backgroundColor: '#F8F9FA',
                      borderBottom: `1px solid #e0e0e0`,
                      borderTopLeftRadius: '8px',
                      borderTopRightRadius: '8px',
                      padding: '16px'
                    }}>
                      <h6 className="card-title mb-0" style={{ 
                        color: COLORS.primary,
                        fontWeight: '600'
                      }}>
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                          width: '30px',
                          height: '30px',
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary
                        }}>
                          <i className="bi bi-clipboard"></i>
                        </div>
                        Informations du type d'incident
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-12">
                          <div className="mb-4">
                            <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                              <i className="bi bi-clipboard me-1" style={{ color: COLORS.secondary }}></i>
                              Nom du type d'incident <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                              <span className="input-group-text" style={{ 
                                backgroundColor: COLORS.lightBg,
                                borderColor: fieldErrors.type_incident ? COLORS.danger : '#e0e0e0',
                                color: fieldErrors.type_incident ? COLORS.danger : COLORS.secondary
                              }}>
                                <i className="bi bi-pencil-square"></i>
                              </span>
                              <input
                                type="text"
                                className={`form-control ${fieldErrors.type_incident ? "is-invalid" : ""}`}
                                name="type_incident"
                                value={formData.type_incident}
                                onChange={handleChange}
                                disabled={loading}
                                required
                                maxLength={250}
                                placeholder="Ex : Panne technique, Intrusion, Incendie..."
                                style={{ 
                                  borderColor: fieldErrors.type_incident ? COLORS.danger : '#e0e0e0',
                                  borderRadius: '0 8px 8px 0',
                                  color: COLORS.darkText
                                }}
                              />
                            </div>
                            {fieldErrors.type_incident && (
                              <div className="text-danger mt-2 small d-flex align-items-center">
                                <i className="bi bi-exclamation-circle me-1"></i>
                                {fieldErrors.type_incident}
                              </div>
                            )}
                            <div className="form-text mt-2 d-flex justify-content-between" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                              <span>
                                <i className="bi bi-info-circle me-1"></i>
                                Nom unique pour identifier le type d'incident
                              </span>
                              <span className={formData.type_incident.length > 250 ? "text-danger" : "text-muted"}>
                                {formData.type_incident.length}/250 caractères
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Récapitulatif */}
                      <div className="mt-4 pt-4 border-top" style={{ borderColor: '#e0e0e0' }}>
                        <h6 className="mb-3" style={{ color: COLORS.primary, fontWeight: '600' }}>
                          <i className="bi bi-list-check me-2"></i>
                          Récapitulatif
                        </h6>
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <div className="p-3 rounded" style={{ 
                              backgroundColor: '#F8F9FA',
                              border: '1px solid #e0e0e0'
                            }}>
                              <small className="text-muted d-block mb-1">Ancien nom :</small>
                              <div className="text-truncate" title={originalData?.type_incident}>
                                {originalData?.type_incident || "Non défini"}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6 mb-3">
                            <div className={`p-3 rounded ${formData.type_incident !== originalData?.type_incident ? 'border border-warning' : ''}`} style={{ 
                              backgroundColor: formData.type_incident !== originalData?.type_incident ? '#fff8e1' : '#F8F9FA',
                              border: formData.type_incident !== originalData?.type_incident ? '1px solid #ffc107' : '1px solid #e0e0e0'
                            }}>
                              <small className="text-muted d-block mb-1">Nouveau nom :</small>
                              <div className="text-truncate" title={formData.type_incident}>
                                {formData.type_incident || <span className="text-danger">Non renseigné</span>}
                              </div>
                              {formData.type_incident !== originalData?.type_incident && formData.type_incident && (
                                <small className="text-warning mt-1 d-flex align-items-center">
                                  <i className="bi bi-exclamation-triangle me-1"></i>
                                  Modifié
                                </small>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Boutons d'action */}
                  <div className="d-flex justify-content-between mt-4 pt-4 border-top" style={{ borderColor: COLORS.lightBg }}>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn fw-semibold"
                        onClick={handleCancel}
                        disabled={loading}
                        style={{
                          color: COLORS.darkText,
                          borderColor: '#e0e0e0',
                          backgroundColor: 'transparent',
                          borderRadius: '8px',
                          padding: '10px 20px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.target.style.backgroundColor = COLORS.lightBg;
                            e.target.style.borderColor = COLORS.primary;
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.borderColor = '#e0e0e0';
                        }}
                      >
                        <i className="bi bi-arrow-left me-2"></i>
                        Retour
                      </button>
                      
                      <button
                        type="button"
                        className="btn fw-semibold"
                        onClick={handleReset}
                        disabled={loading}
                        style={{
                          color: COLORS.primary,
                          borderColor: COLORS.primary,
                          backgroundColor: 'transparent',
                          borderRadius: '8px',
                          padding: '10px 20px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.target.style.backgroundColor = '#E3F2FD';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                        }}
                      >
                        <i className="bi bi-arrow-clockwise me-2"></i>
                        Réinitialiser
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .form-label,
        .alert strong {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .form-control,
        .form-text,
        .alert,
        .table,
        small {
          font-family: 'Roboto', sans-serif;
        }
        
        .form-control:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(11, 60, 93, 0.25);
        }
        
        .btn,
        .form-control {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:disabled {
          background-color: ${COLORS.lightBg};
          cursor: not-allowed;
        }
        
        .input-group-text {
          transition: all 0.2s ease-in-out;
        }
        
        .is-invalid {
          border-color: ${COLORS.danger} !important;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
        }
      `}</style>
    </Layout>
  );
};

export default EditIncidentType;